/**
 * DexTop token list service.
 *
 * Fetches https://raw.githubusercontent.com/GoDexTop/dextopList/refs/heads/main/dextop-list.json
 * once per session, caches in memory.  Provides:
 *   - getTokenList()          — all tokens for the active network (chainId 369 = mainnet, 943 = testnet)
 *   - getTokenByAddress()     — single-token lookup (case-insensitive)
 *   - getTokenLogoUri()       — returns logoURI string or null
 */

const LIST_URL = 'https://raw.githubusercontent.com/GoDexTop/dextopList/refs/heads/main/dextop-list.json';

export type DexTopToken = {
  name: string;
  symbol: string;
  address: string;
  chainId: number;
  decimals: number;
  logoURI: string;
};

type TokenList = {
  tokens: DexTopToken[];
};

const BLOCKED_SYMBOLS = new Set(['ROB']);
const BLOCKED_ADDRESSES = new Set([
  '0x1c2766f5949a4aa5d4cf0439067051135ffc1b28',
]);

function isBlockedToken(token: Partial<DexTopToken>): boolean {
  const symbol = String(token.symbol || '').trim().toUpperCase();
  const address = String(token.address || '').trim().toLowerCase();
  return BLOCKED_SYMBOLS.has(symbol) || BLOCKED_ADDRESSES.has(address);
}

let cachedList: DexTopToken[] | null = null;
let fetchPromise: Promise<DexTopToken[]> | null = null;

/**
 * Fetch (and cache) the full DexTop token list.
 * Resolves to an empty array on network error so callers never throw.
 */
export async function fetchTokenList(): Promise<DexTopToken[]> {
  if (cachedList !== null) return cachedList;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async (): Promise<DexTopToken[]> => {
    try {
      const res = await fetch(LIST_URL, { cache: 'default' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: TokenList = await res.json();
      const tokens = (json.tokens ?? [])
        .filter((t) => typeof t.address === 'string' && typeof t.symbol === 'string')
        .filter((t) => !isBlockedToken(t));
      cachedList = tokens;
      return tokens;
    } catch {
      cachedList = [];
      return [];
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

/**
 * All tokens for a given chainId, sorted: stables first (by symbol), then alphabetically.
 */
export async function getTokensForChain(chainId: number): Promise<DexTopToken[]> {
  const all = await fetchTokenList();
  const forChain = all.filter((t) => t.chainId === chainId);
  // Some lists may temporarily omit a network (e.g. testnet). Fall back to
  // the canonical mainnet set so the picker still renders useful options.
  const workingSet = forChain.length > 0 ? forChain : all.filter((t) => t.chainId === 369);
  const STABLE_ORDER = ['USDC', 'DAI', 'USDT'];
  workingSet.sort((a, b) => {
    const ai = STABLE_ORDER.indexOf(a.symbol.toUpperCase());
    const bi = STABLE_ORDER.indexOf(b.symbol.toUpperCase());
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.symbol.localeCompare(b.symbol);
  });
  return workingSet;
}

/**
 * Look up a token by address (case-insensitive).
 */
export async function getTokenByAddress(address: string): Promise<DexTopToken | null> {
  const all = await fetchTokenList();
  const lower = address.toLowerCase();
  return all.find((t) => t.address.toLowerCase() === lower) ?? null;
}

/**
 * Synchronous lookup from already-loaded cache — returns null if list not yet loaded.
 */
export function getTokenByAddressSync(address: string): DexTopToken | null {
  if (!cachedList) return null;
  const lower = address.toLowerCase();
  return cachedList.find((t) => t.address.toLowerCase() === lower) ?? null;
}

/**
 * Get a token's logoURI by address.  Returns null if not in list.
 */
export async function getTokenLogoUri(address: string): Promise<string | null> {
  const token = await getTokenByAddress(address);
  return token?.logoURI ?? null;
}

/**
 * Synchronous lookup from already-loaded cache — returns null if list not yet loaded.
 */
export function getTokenLogoUriSync(address: string): string | null {
  if (!cachedList) return null;
  const lower = address.toLowerCase();
  return cachedList.find((t) => t.address.toLowerCase() === lower)?.logoURI ?? null;
}
